import * as React from "react";
import { useRef, useState, useEffect, useCallback, createContext, useContext } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { motion } from "motion/react";
import { cn } from "@/components/ui/cn";

export type ClubTabValue = "happening" | "upcoming";

// Context to allow triggers to communicate with the list
interface TabsContextValue {
  registerTab: (value: ClubTabValue, element: HTMLButtonElement | null) => void;
  onTabPointerDown: (value: ClubTabValue) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

interface ClubTabsProps {
  defaultValue?: ClubTabValue;
  value?: ClubTabValue;
  onValueChange?: (value: ClubTabValue) => void;
  children: React.ReactNode;
  className?: string;
}

export function ClubTabs({
  defaultValue = "happening",
  value,
  onValueChange,
  children,
  className,
}: ClubTabsProps) {
  // Handle controlled vs uncontrolled mode
  if (value !== undefined && onValueChange !== undefined) {
    // Controlled mode: value and onValueChange both provided
    return (
      <Tabs.Root
        value={value}
        onValueChange={onValueChange as (value: string) => void}
        className={cn("w-full", className)}
      >
        {children}
      </Tabs.Root>
    );
  }

  // Uncontrolled mode: use defaultValue
  return (
    <Tabs.Root
      defaultValue={defaultValue}
      className={cn("w-full", className)}
    >
      {children}
    </Tabs.Root>
  );
}

interface ClubTabsListProps {
  className?: string;
}

export function ClubTabsList({ className }: ClubTabsListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<ClubTabValue, HTMLButtonElement | null>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  // Calculate indicator position for a given tab element
  const updateIndicatorForElement = useCallback((tabElement: HTMLElement) => {
    if (!listRef.current) return;
    const listRect = listRef.current.getBoundingClientRect();
    const tabRect = tabElement.getBoundingClientRect();
    setIndicatorStyle({
      left: tabRect.left - listRect.left,
      width: tabRect.width,
    });
    setIsInitialized(true);
  }, []);

  // Register tab refs
  const registerTab = useCallback((value: ClubTabValue, element: HTMLButtonElement | null) => {
    tabRefs.current.set(value, element);
  }, []);

  // Handle immediate animation on pointer down
  const onTabPointerDown = useCallback((value: ClubTabValue) => {
    const tabElement = tabRefs.current.get(value);
    if (tabElement) {
      updateIndicatorForElement(tabElement);
    }
  }, [updateIndicatorForElement]);

  // Initial measurement based on active tab
  useEffect(() => {
    const updateFromActiveTab = () => {
      if (!listRef.current) return;
      const activeTab = listRef.current.querySelector('[data-state="active"]') as HTMLElement;
      if (activeTab) {
        updateIndicatorForElement(activeTab);
      }
    };

    // Initial measurement
    updateFromActiveTab();

    // Update on resize
    window.addEventListener('resize', updateFromActiveTab);

    return () => {
      window.removeEventListener('resize', updateFromActiveTab);
    };
  }, [updateIndicatorForElement]);

  const contextValue: TabsContextValue = { registerTab, onTabPointerDown };

  return (
    <TabsContext.Provider value={contextValue}>
      <div className={cn("sticky top-0 z-10 py-3 bg-background", className)}>
        <Tabs.List
          ref={listRef}
          className="relative inline-flex gap-2 p-1 rounded-[16px] bg-surface border border-border-subtle"
        >
          {/* Animated indicator */}
          {isInitialized && (
            <motion.div
              className="absolute top-1 bottom-1 bg-background border border-border-tertiary rounded-[12px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06)]"
              initial={false}
              animate={{
                left: indicatorStyle.left,
                width: indicatorStyle.width,
              }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 35,
              }}
            />
          )}
          <ClubTabsTrigger value="happening">What&apos;s happening</ClubTabsTrigger>
          <ClubTabsTrigger value="upcoming">Upcoming</ClubTabsTrigger>
        </Tabs.List>
      </div>
    </TabsContext.Provider>
  );
}

interface ClubTabsTriggerProps {
  value: ClubTabValue;
  children: React.ReactNode;
  className?: string;
}

function ClubTabsTrigger({ value, children, className }: ClubTabsTriggerProps) {
  const context = useContext(TabsContext);
  const ref = useRef<HTMLButtonElement>(null);

  // Register this tab with the parent
  useEffect(() => {
    context?.registerTab(value, ref.current);
    return () => {
      context?.registerTab(value, null);
    };
  }, [context, value]);

  return (
    <Tabs.Trigger
      ref={ref}
      value={value}
      onPointerDown={() => context?.onTabPointerDown(value)}
      className={cn(
        // Base styles per Figma exactly: h-32px, min-w-88px, px-24px, py-8px, 16px font, normal line-height
        "h-[32px] min-w-[88px] px-[24px] py-[8px] text-[16px] font-medium leading-normal cursor-pointer",
        // Flex centering to ensure text is vertically centered
        "flex items-center justify-center",
        // Position relative to appear above the animated indicator
        "relative z-10",
        // Inactive state text color
        "text-text-secondary",
        // Smooth text color transition
        "transition-colors duration-200",
        // Active state: only text color changes (indicator handles background/border)
        "data-[state=active]:text-text-primary",
        "focus-visible:outline-none",
        className
      )}
    >
      {children}
    </Tabs.Trigger>
  );
}

interface ClubTabsContentProps {
  value: ClubTabValue;
  children: React.ReactNode;
  className?: string;
}

export function ClubTabsContent({
  value,
  children,
  className,
}: ClubTabsContentProps) {
  return (
    <Tabs.Content
      value={value}
      className={cn("focus-visible:outline-none", className)}
    >
      {children}
    </Tabs.Content>
  );
}
